import type { ReactNode } from "react";

interface AdminPageProps {
  /** Window, scope filters, search, result count, status, refresh. */
  filters?: ReactNode;
  /**
   * A full-width explanation of the numbers below — why a window is empty, why
   * a figure is missing. Above the KPIs because every region under it keys its
   * empty state on a row count, which cannot tell "nothing ran" from "nothing
   * is recorded".
   */
  notice?: ReactNode;
  /** Three to six metrics plus one optional pinned live metric. */
  kpis?: ReactNode;
  /** The collapsible chart panel. */
  chart?: ReactNode;
  /** One or two titled panels, side by side, reading against each other. */
  breakdown?: ReactNode;
  /** The full-width list of individual rows, and the way into one of them. */
  detail?: ReactNode;
  /** Provenance and reconciliation — read after the data, never before it. */
  footnote?: ReactNode;
  /** Drawers, sheets and dialogs. Outside the sequence; they own no space. */
  overlays?: ReactNode;
}

/**
 * The one layout every admin tab renders.
 *
 * It opens on the filter bar: the tab strip above already names the tab, so a
 * title band under it spent a row repeating the word the reader just clicked.
 *
 * Regions arrive as slots rather than children so that the sequence is a
 * property of this file and not of the page: a page picks a subset, and there
 * is no argument it can pass to reorder them. Adding a region means adding it
 * here, for every tab at once.
 *
 * The column is sized to its own content rather than to the tab panel. Every
 * panel is `shrink-0`, so a height-constrained column would end at the viewport
 * and strand its padding above the panels that overflow past it, leaving the
 * last one flush against the end of the scroll. A region that would otherwise
 * grow without bound caps itself and scrolls in place — see `AdminDataPanel`.
 */
export function AdminPage({
  filters,
  notice,
  kpis,
  chart,
  breakdown,
  detail,
  footnote,
  overlays,
}: AdminPageProps) {
  return (
    <div className="flex shrink-0 flex-col gap-4 pb-6">
      {filters}
      {notice}
      {kpis}
      {chart}
      {breakdown}
      {detail}
      {footnote}
      {overlays}
    </div>
  );
}
